/**
 * PATH: packages/email-hub/src/feature/lib/markupGeometry.ts
 * FILE_ID: EMAIL-HUB-LIB-MARKUP-GEOMETRY-001
 * PURPOSE: Pure geometry helpers for the markup drawer's *select* tool —
 *          hit-testing a click against an existing stroke, translating a
 *          stroke by a delta (drag-to-move), reading/replacing a stroke's
 *          text label (double-click-to-edit), and computing a stroke's
 *          bounding box (the dashed selection outline).
 * WHY:     Operator (2026-05-31): "I should be able to add text and move
 *          it". The original drawer was draw-only — once a label landed
 *          you couldn't nudge or fix it. These helpers stay out of
 *          markupCanvas.ts so that file remains the pure *render* layer
 *          and this stays the pure *interaction* layer (both unit-testable
 *          without a DOM).
 * RELATED:
 *   - lib/markupCanvas.ts (Stroke types + renderers)
 *   - components/MarkupDrawer.tsx (wires pointer events to these)
 */

import type { Point, Stroke } from './markupCanvas'

export interface Bounds {
  x: number
  y: number
  w: number
  h: number
}

/** Shortest distance from point `p` to the segment `a`→`b`. */
export function distanceToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const lenSq = dx * dx + dy * dy
  if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y)
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy))
}

/** Approximate on-canvas bounds of a stroke. `measureText` lets the
 *  caller pass a real canvas measurer for text/measure labels; when it's
 *  absent we fall back to a width estimate so the helper still works in a
 *  headless test. */
export function strokeBounds(
  stroke: Stroke,
  measureText?: (text: string, fontSize: number) => number,
): Bounds {
  if (stroke.type === 'text') {
    const w = measureText
      ? measureText(stroke.text, stroke.fontSize)
      : stroke.text.length * stroke.fontSize * 0.6
    return { x: stroke.position.x, y: stroke.position.y, w, h: stroke.fontSize }
  }
  if (stroke.type === 'freehand') {
    const xs = stroke.points.map((q) => q.x)
    const ys = stroke.points.map((q) => q.y)
    const minX = Math.min(...xs)
    const minY = Math.min(...ys)
    return { x: minX, y: minY, w: Math.max(...xs) - minX, h: Math.max(...ys) - minY }
  }
  // arrow | rect | measure — all defined by start/end.
  const { start, end } = stroke
  const minX = Math.min(start.x, end.x)
  const minY = Math.min(start.y, end.y)
  return { x: minX, y: minY, w: Math.abs(end.x - start.x), h: Math.abs(end.y - start.y) }
}

/** Is point `p` within `tol` px of stroke `s`? */
export function hitTestStroke(
  s: Stroke,
  p: Point,
  tol = 10,
  measureText?: (text: string, fontSize: number) => number,
): boolean {
  if (s.type === 'text') {
    const b = strokeBounds(s, measureText)
    return (
      p.x >= b.x - tol && p.x <= b.x + b.w + tol &&
      p.y >= b.y - tol && p.y <= b.y + b.h + tol
    )
  }
  if (s.type === 'arrow' || s.type === 'measure') {
    return distanceToSegment(p, s.start, s.end) <= tol
  }
  if (s.type === 'rect') {
    const { start, end } = s
    const x1 = Math.min(start.x, end.x)
    const y1 = Math.min(start.y, end.y)
    const x2 = Math.max(start.x, end.x)
    const y2 = Math.max(start.y, end.y)
    const corners: [Point, Point][] = [
      [{ x: x1, y: y1 }, { x: x2, y: y1 }],
      [{ x: x2, y: y1 }, { x: x2, y: y2 }],
      [{ x: x2, y: y2 }, { x: x1, y: y2 }],
      [{ x: x1, y: y2 }, { x: x1, y: y1 }],
    ]
    return corners.some(([a, b]) => distanceToSegment(p, a, b) <= tol)
  }
  // freehand — near any segment of the polyline.
  for (let i = 1; i < s.points.length; i += 1) {
    if (distanceToSegment(p, s.points[i - 1], s.points[i]) <= tol) return true
  }
  return false
}

/** Index of the topmost (last-drawn) stroke hit by `p`, or -1. */
export function findTopStrokeAt(
  strokes: ReadonlyArray<Stroke>,
  p: Point,
  tol = 10,
  measureText?: (text: string, fontSize: number) => number,
): number {
  for (let i = strokes.length - 1; i >= 0; i -= 1) {
    if (hitTestStroke(strokes[i], p, tol, measureText)) return i
  }
  return -1
}

/** Return a copy of `s` shifted by (dx, dy). */
export function translateStroke(s: Stroke, dx: number, dy: number): Stroke {
  const shift = (q: Point): Point => ({ x: q.x + dx, y: q.y + dy })
  if (s.type === 'text') return { ...s, position: shift(s.position) }
  if (s.type === 'freehand') return { ...s, points: s.points.map(shift) }
  return { ...s, start: shift(s.start), end: shift(s.end) }
}

/** The editable label of a stroke (text body / dimension label), or null
 *  for strokes that have no text. */
export function getStrokeLabel(s: Stroke): string | null {
  if (s.type === 'text') return s.text
  if (s.type === 'measure') return s.label
  return null
}

/** Return a copy of `s` with its label replaced (no-op for label-less
 *  stroke types). */
export function setStrokeLabel(s: Stroke, label: string): Stroke {
  if (s.type === 'text') return { ...s, text: label }
  if (s.type === 'measure') return { ...s, label }
  return s
}
